import { call, put, select, take } from 'redux-saga/effects';
import { EntityTypes } from 'constants/entities';
import { attemptRequest, moduleRequest } from 'core/services/product';
import { IAttemptAction } from 'types/action/player';
import { updateState } from 'core/actions/product/state';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { RootState } from 'types';
import { selectActionFirst } from 'core/selectors/product/action/actionFirst';
import { selectModuleAssign } from 'core/selectors/product/module';
import { IModuleAssign, ModuleType } from 'types/domain/module';
import { ISessionReducer, IToken, Mode } from 'core/slices/session';
import { IRegisterReducer, next, preview, registerAttempt, registerModule, setIndex } from 'core/slices/register';
import { getRequest } from 'core/services/admin';
import { loadModule } from '../module/load';
import { selectToken } from 'core/selectors/product/token';
import { selectRegister } from 'core/selectors/product/register';
import { ISearchReducer } from 'core/slices/search';
import { selectSession } from 'core/selectors/product/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { updatePreview } from 'core/slices/preview';
import { IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { findLastIndex } from 'lodash';

export function* startModule(sessionAttemptId, moduleId, actionId, isSearch = false, autoStart = true) {
  const mId = yield yield select((state: RootState) => state.state[EntityTypes.MODULE]);
  const aId = yield yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  const sId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const token: IToken = yield select(selectToken);
  const mode: Mode = yield select(selectSessionMode);

  if (moduleId && mId !== moduleId) yield put(yield call(updateState, { type: EntityTypes.MODULE, id: moduleId }));

  const module: IModuleAssign = yield select(state => selectModuleAssign(state, moduleId));

  if ([ModuleType.WEBSITE, ModuleType.YOUTUBE, ModuleType.SCORM].includes(module.type)) {
    yield put(yield call(registerModule, { moduleId, sessionAttemptId }));
    const register: IRegisterReducer = yield select(selectRegister);
    if (register.isNext) yield put(yield call(next, { culprit: 'startModule' }));
    return;
  }

  let loading = yield loadModule(moduleId);
  while (!preview && loading) {
    const action = yield take([moduleRequest.fulfilled, getRequest.fulfilled]);
    loading = action.payload.id !== moduleId;
  }

  if (isSearch) {
    const offlineId = yield select(selectOfflineId);
    const search: ISearchReducer = yield select((state: RootState) => state.core.search);
    const session: ISessionReducer = yield select(selectSession);
    yield put(
      yield call(attemptRequest, {
        offlineId,
        moduleId: module.id,
        type: module.type,
        searchId: search.response.searchId,
        sessionId: session.session.id,
      } as IAttemptAction),
    );
  } else if (mode === Mode.PREVIEW) {
    yield put(yield call(updateState, { type: EntityTypes.SESSION_ATTEMPT, id: sessionAttemptId }));
    yield put(yield call(updatePreview, { type: EntityTypes.MODULE, id: module.id, data: { sessionAttemptId } }));
    const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);
    yield put(
      yield call(registerAttempt, {
        sessionAttemptId,
        courseProgressId,
        moduleId: module.id,
        type: module.type,
        token: token?.token,
      }),
    );
  } else if (mode === Mode.LEARNING) {
    yield put(yield call(updateState, { type: EntityTypes.SESSION_ATTEMPT, id: module.sessionAttempt.id }));
    const register: IRegisterReducer = yield select(selectRegister);

    //if (!register.attempts[module.sessionAttempt.id])
    yield put(
      yield call(registerAttempt, {
        sessionAttemptId: module.sessionAttempt.id,
        courseProgressId: token?.courseProgressId,
        moduleId: module.id,
        type: module.type,
        token: token?.token,
      }),
    );
  }

  if (autoStart) {
    if (!isNaN(actionId) && actionId !== null) yield startAction(actionId);
    else {
      const firstAction = yield select(selectActionFirst);

      if (firstAction && firstAction.id !== aId) yield startAction(firstAction.id);
    }
  }
}

export function* startAction(actionId) {
  if (actionId === undefined) return console.warn('startAction undefined');
  //if we need this, clear out actionId when exit preview
  //const aId = yield yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  //if (aId !== actionId)
  yield put(yield call(updateState, { type: EntityTypes.ACTION, id: actionId, culprit: 4 }));

  const register: IRegisterReducer = yield select(selectRegister);
  if (register.isNext) yield put(yield call(next, { culprit: 'startAction' }));

  const feed = yield select(selectActionFeed);

  const index = findLastIndex(feed.feed, (item: IActionFeedItem) => item.actionId === Number(actionId));

  if (index > -1 && index !== feed.index) yield put(yield call(setIndex, index));
}
